import axios from "axios";
import { safeNext } from "@/lib/nav";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export const API_BASE = `${BACKEND}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

export function errorMessage(error, fallback = "Something went wrong.") {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return detail.message;
  if (Array.isArray(detail) && detail.length) {
    return detail[0]?.msg || fallback;
  }
  if (error?.message === "Network Error") {
    return "Could not reach the editor. Check your connection and try again.";
  }
  return fallback;
}

export function blockingFromError(error) {
  const detail = error?.response?.data?.detail;
  if (detail && Array.isArray(detail.blocking)) return detail.blocking;
  return [];
}

/**
 * Sign-in.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * Two routes exist. `/auth/config` says which one is live, so the button never has to guess:
 *
 * - `google` (in use): Review Presence's own Google client. The browser goes to the backend,
 *   which builds the Google URL server side - the client id and the registered callback address
 *   live there, not here.
 * - `managed` (dormant): Emergent's hosted sign-in, kept switched off as a recovery path. The
 *   address to come back to is read from the browser at the moment of the click, so it works on
 *   any address the editor is served from.
 */
export const MANAGED_SIGN_IN_URL = "https://auth.emergentagent.com/";

/** Dormant recovery path: only reachable while MANAGED_AUTH_ENABLED is true on the backend. */
export const managedSignInUrl = (next = "/") => {
  const path = safeNext(next);
  const redirectUrl = window.location.origin + path;
  return `${MANAGED_SIGN_IN_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
};

/** In use: the agency's own Google client, brokered by the backend. */
export const googleStartUrl = (next = "/") =>
  `${API_BASE}/auth/google/start?next=${encodeURIComponent(safeNext(next))}`;

/**
 * Dormant recovery path only: the managed flow returns its one-time id in the URL fragment,
 * never as a query parameter. The agency's own Google client does not use this at all - its
 * callback is handled entirely by the backend.
 */
export const readSessionIdFromHash = (hash = "") => {
  const raw = String(hash).replace(/^#/, "");
  if (!raw) return null;
  return new URLSearchParams(raw).get("session_id");
};
